import { RefetchQueriesInclude, useLazyQuery, useQuery } from "@apollo/client";
import Download from "@mui/icons-material/Download";
import FindReplaceIcon from "@mui/icons-material/FindReplace";
import {
  Box,
  Breakpoint,
  Button,
  Checkbox,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { DocumentPreview } from "components/DocumentPreview";
import { Dropzone } from "components/Dropzone";
import { FormAutocomplete } from "components/Forms/FormAutocomplete";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { TagPicker } from "components/TagPicker";
import { CA_DocumentOption } from "encamp-compliance-engine/src/types/facilityStateFieldTypes";
import { prettyPrintDocumentType } from "encamp-shared/src/utils/prettyPrintDocumentType";
import { gql } from "generated-graphql";
import {
  CaDocumentType,
  DocumentAssociationsInput,
  DocumentInput,
  DocumentType,
  FacilityDocumentInput,
  FileFormatsByDocumentType,
  Document as GqlDocument,
  Issue,
  TagInput,
  TagPickerFragment,
  TagType,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useDocumentDownload } from "hooks/useDocumentDownload";
import { useDocumentUpload } from "hooks/useDocumentUpload";
import { useFilenameStandardization } from "hooks/useFilenameStandardization";
import { useUpsertDocument } from "hooks/useUpsertDocument";
import { useValidatingForm } from "hooks/useValidatingForm";
import GET_DOCUMENT_DOWNLOAD_LINK_QUERY from "queries/getDocumentDownloadLink";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  isHmbpDocumentType,
  useHmbpDocumentOptions,
  useHmbpFeature,
  useHmbpStateFieldMutation,
} from "routes/Customer/Chemicals/Report/useHmbp";
import { useDocumentIssuesForModel } from "routes/Customer/Chemicals/Report/useReport";
import { useDocumentInputValidation } from "routes/Customer/Chemicals/Report/validationHooks/useDocumentInputValidation";
import { documentTypeToLabel } from "util/constants";
import { hasCriticalIssues } from "util/forms";
import {
  FacilityDocumentPicker,
  FacilityDocumentPickerItem,
} from "./FacilityDocumentPicker";
import { FacilityDocumentTypePicker } from "./FacilityDocumentTypePicker";
import { DateField } from "./Forms/DateField";
import { MultipleFacilityPicker } from "./MultipleFacilityPicker";

const GET_ACCEPTABLE_FILE_FORMATS_BY_DOCUMENT_TYPE = gql(`
  query GetAcceptableFileFormatsByDocumentType($tenantId: ID!, $facilityId: ID!) {
    getAcceptableFileFormatsByDocumentType(tenantId: $tenantId, facilityId: $facilityId) {
      documentType
      fileFormats
    }
  }
`);

const GET_UPSERTED_DOCUMENT = gql(`
  query GetDocumentForAddEdit($id: ID!) {
    document(id: $id) {
      id
      title
      description
      storageLink
      documentType
      updatedAt
      createdAt
      authoredAt
      reportId
      documentTags {
        id
        name
        tenantId
        isDisabled
        type
      }
    }
  }
`);

type AddEditDocumentDialogProps = {
  onSubmit?: (
    document: Pick<
      GqlDocument,
      "id" | "fileExtension" | "title" | "documentType"
    >,
    dialogState: AddEditDocumentDialogState
  ) => void;
  dialogState: AddEditDocumentDialogState;
  setDialogState: Dispatch<SetStateAction<AddEditDocumentDialogState>>;
  documentTypeOptions?: DocumentType[];
  activityPickerOptions?: {
    id: string;
    title: string;
  }[];
  tenantId: string;
  reportId?: string;
  state?: string | null;
  showFacilityPicker?: boolean;
  showTagPicker?: boolean;
  refetchQueries?: RefetchQueriesInclude;
  facilityDocumentIssues?: Issue[];
  showAttestation?: boolean;
  generateTitleOnCreate?: boolean;
  isReadOnly?: boolean;
  dialogHeight?: number;
  disableDocumentTypeSelection?: boolean;
};

export enum FormState {
  UPLOAD = "Upload",
  FORM = "Form",
}

export const defaultAddEditDocumentDialogState: AddEditDocumentDialogState = {
  open: false,
  saving: false,
  step: FormState.UPLOAD,
};

export enum EditMode {
  ADD = "Add",
  ADD_OR_PICK = "AddOrPick",
  EDIT = "Edit",
}

export type AddEditDocumentDialogState = {
  facilities?: FacilityDocumentInput[];
  documentId?: string;
  open: boolean;
  mode?: EditMode;
  step?: FormState;
  title?: string;
  documentType?: DocumentType;
  storageLink?: string;
  activityId?: string;
  fileExtension?: string | null | undefined;
  selectedFile?: File;
  documentTags?: TagPickerFragment[];
  saving: boolean;
  currentSubmissionDocument?: boolean;
  authoredAt?: string;
  description?: string | null | undefined;
  isAddingFromIssue?: boolean;
  isReadOnly?: boolean;
};

// 1. select a document
// 2. upload a new document

export type DocumentFormData = Omit<DocumentInput, "documentTags"> & {
  fileSize?: number;

  // gql-gen generates this attribute with type `InputMaybe<T>` which means it could be null
  // but it can only be undefined or an array of tag inputs
  documentTags?: TagInput[];
};

export function AddEditDocumentDialog({
  onSubmit,
  dialogState,
  setDialogState,
  documentTypeOptions,
  activityPickerOptions,
  tenantId,
  reportId,
  state,
  showFacilityPicker,
  showTagPicker,
  facilityDocumentIssues,
  showAttestation,
  generateTitleOnCreate,
  refetchQueries,
  isReadOnly = false,
  dialogHeight = 960,
  disableDocumentTypeSelection = false,
}: AddEditDocumentDialogProps) {
  const theme = useTheme();
  const alerts = useAlerts();
  const { user } = useCurrentUser();
  const { fetchAndHandleDownload, clearDownloadLink } = useDocumentDownload();
  const navigate = useNavigate();
  const hmbpFeature = useHmbpFeature(state ?? "");

  const { setDocumentOptionToUpload, loading: loadingStateFields } =
    useHmbpStateFieldMutation();
  const [pickedDocument, setPickedDocument] =
    useState<FacilityDocumentPickerItem | null>(null);

  const shouldFieldsBeDisabled =
    dialogState.mode === EditMode.ADD_OR_PICK && !!pickedDocument;
  const [attestation, setAttestation] = useState<boolean>(false);

  const [hasInvalidFileExtension, setHasInvalidFileExtension] =
    useState<boolean>(false);

  const initialIssues = useDocumentIssuesForModel(dialogState.documentId ?? "");
  const defaultValues: DocumentFormData = useMemo(
    () => ({
      title: dialogState.title ?? "",
      documentType: dialogState.documentType,
      activityId: dialogState.activityId,
      fileExtension: dialogState.fileExtension,
      storageLink: dialogState.storageLink,
      currentSubmissionDocument: dialogState.currentSubmissionDocument,
      facilities: dialogState.facilities || [],
      documentTags: dialogState.documentTags ?? [],
      authoredAt: dialogState.authoredAt,
      description: dialogState.description,
    }),
    [
      dialogState.title,
      dialogState.documentType,
      dialogState.activityId,
      dialogState.fileExtension,
      dialogState.storageLink,
      dialogState.currentSubmissionDocument,
      dialogState.facilities,
      dialogState.documentTags,
      dialogState.authoredAt,
      dialogState.description,
    ]
  );

  const { data, loading: isLoadingDownloadLink } = useQuery(
    GET_DOCUMENT_DOWNLOAD_LINK_QUERY,
    {
      variables: { id: dialogState.documentId ?? "" },
      skip: !dialogState.documentId,
    }
  );

  const { uploadFile, isUploading } = useDocumentUpload({
    generateTitleOnCreate: generateTitleOnCreate,
    onSuccess: (document) => {
      onClose();
      if (onSubmit) {
        onSubmit(document, dialogState);
      }
      reset();
      setAttestation(false);
      alerts.success("Successfully uploaded the file");
    },
    onError: (error) => {
      alerts.error("An error occurred while uploading the document.", error);
    },
    refetchQueries,
  });

  const { upsertDocument } = useUpsertDocument();
  const [fetchUpsertedDocument] = useLazyQuery(GET_UPSERTED_DOCUMENT);

  const form = useValidatingForm<DocumentFormData>(
    defaultValues,
    initialIssues,
    useDocumentInputValidation(),
    [],
    { isDisplayOnly: isReadOnly }
  );

  const {
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    issues,
    trigger,
    setError,
    clearErrors,
  } = form;

  const watchedActivityId = watch("activityId");
  const watchTitle = watch("title");
  const watchDocumentType = watch("documentType");
  const watchFileExtension = watch("fileExtension");

  const documentOption = useHmbpDocumentOptions(watchDocumentType);
  const showHmbpWarning =
    hmbpFeature &&
    (documentOption ===
      CA_DocumentOption[CA_DocumentOption["Provided Elsewhere"]] ||
      documentOption === CA_DocumentOption[CA_DocumentOption["Exempt"]]);

  const isInvalidSds =
    watchDocumentType === DocumentType.SafetyDataSheet &&
    watchFileExtension?.toLowerCase() !== "pdf";

  const uncheckedRequiredAttestation = showAttestation && !attestation;

  const saveDisabled = useMemo(() => {
    return (
      hasCriticalIssues(issues) ||
      isInvalidSds ||
      uncheckedRequiredAttestation ||
      (!form.formState.isDirty && !pickedDocument)
    );
  }, [
    issues,
    isInvalidSds,
    uncheckedRequiredAttestation,
    form.formState.isDirty,
    pickedDocument,
  ]);

  useEffect(() => {
    if (watchTitle && (!watchFileExtension || watchFileExtension === "")) {
      const match = /\.[0-9a-z]+$/i.exec(watchTitle);
      if (match) {
        setValue("fileExtension", match[0].slice(1)); // Remove the leading "."
      }
    }
  }, [watchTitle, setValue, watchFileExtension]);

  const activitySelectItems = useMemo(() => {
    if (activityPickerOptions === undefined) {
      return undefined;
    }
    const options = activityPickerOptions.map((v) => ({
      display: v.title,
      value: v.id as string | null,
    }));
    if (watchedActivityId) {
      options.unshift({ display: "None", value: null });
    }
    return options;
  }, [watchedActivityId, activityPickerOptions]);

  useEffect(() => {
    let msg = "";
    const titleAndExtensionIssues = issues?.filter(
      (issue) => issue.key === "title" || issue.key === "fileExtension"
    );

    if (!titleAndExtensionIssues?.length) {
      clearErrors("title");
      return;
    }

    for (const issue of titleAndExtensionIssues ?? []) {
      if (msg === "") {
        msg += issue.message;
      } else {
        msg += "|" + issue.message;
      }
    }

    setError("title", { message: msg });
  }, [issues, setError, clearErrors]);

  // dialogState.selectedFile is set when we are adding a document
  // data?.getDocumentDownloadLink is set when we are editing a document
  // There are both b/c one is a File and the other is a string link, and it's not worth the
  // pain to unify them
  const hasSelectedFile = useMemo(() => {
    return !!(dialogState.selectedFile || data?.getDocumentDownloadLink);
  }, [dialogState.selectedFile, data?.getDocumentDownloadLink]);

  // Memoize the URL so that the document preview will not unnecessarily reload which causes it to flash
  const fileUrl = useMemo(() => {
    return dialogState.selectedFile
      ? URL.createObjectURL(dialogState.selectedFile ?? new Blob())
      : data?.getDocumentDownloadLink ?? "";
  }, [dialogState.selectedFile, data?.getDocumentDownloadLink]);

  const facilityName = useMemo(() => {
    const facilities = dialogState.facilities;
    if (facilities?.length === 1) {
      return facilities[0].name || "";
    }
    return "";
  }, [dialogState.facilities]);

  // Use filename standardization hook
  const filenameStandardization = useFilenameStandardization({
    originalFilename: dialogState.selectedFile?.name,
    documentType: watchDocumentType ?? DocumentType.Other,
    facilityName,
    date: new Date(),
    isEdit: dialogState.mode === EditMode.EDIT,
  });

  useEffect(() => {
    if (dialogState.selectedFile) {
      setValue("title", filenameStandardization.standardizedFilename, {
        shouldDirty: true,
      });
    }
  }, [
    dialogState.selectedFile,
    filenameStandardization.standardizedFilename,
    setValue,
  ]);

  const removeDocumentIdFromUrl = () => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    // Only do this if we are on the documents list page and there is a document id in the url
    if (pathSegments.length === 4 && pathSegments[2] === "documents") {
      pathSegments.pop(); // Remove the last segment

      const newPath = `/${pathSegments.join("/")}${location.search}`;
      navigate(newPath);
    }
  };

  const onClose = () => {
    setDialogState(defaultAddEditDocumentDialogState);
    reset();
    setAttestation(false);
    setHasInvalidFileExtension(false);
    removeDocumentIdFromUrl();
  };

  const caDocumentTypes = Object.values(
    CaDocumentType
  ) as unknown as DocumentType[];
  const defaultDocumentTypeOptions = Object.values(DocumentType).filter(
    (x) => !caDocumentTypes.includes(x)
  );

  const selectedDocumentType = dialogState.documentType;

  // First deduplicate the document types, then map to options
  const uniqueDocumentTypes = [
    ...new Set([
      ...(documentTypeOptions ?? defaultDocumentTypeOptions),
      // include the already selected document type in case it's not in the list
      ...(selectedDocumentType ? [selectedDocumentType] : []),
    ]),
  ];

  const documentPickerOptions = uniqueDocumentTypes
    .map((v) => ({
      label: documentTypeToLabel(v),
      value: v,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleFileSelect = async (files: File[]) => {
    const file = files?.[0];
    if (!file) {
      return;
    }

    const match = /\.[0-9a-z]+$/i.exec(file.name);
    const fileExtension = match ? match[0].slice(1) : "";

    // Check if the file extension is valid for the selected document type
    if (dialogState.documentType && acceptableFileFormats.length > 0) {
      const isValidExtension = acceptableFileFormats.includes(
        fileExtension.toLowerCase()
      );
      setHasInvalidFileExtension(!isValidExtension);
      if (!isValidExtension) {
        return;
      }
    } else {
      setHasInvalidFileExtension(false);
    }

    // Set form values with shouldDirty: true to mark form as dirty
    // This is needed because programmatic setValue calls don't mark form as dirty by default,
    // but we need the form to recognize these changes for validation and submission logic
    setValue("fileExtension", fileExtension, { shouldDirty: true });
    setValue("fileSize", file.size, { shouldDirty: true });

    setDialogState((state) => ({
      ...state,
      selectedFile: file,
      step: FormState.FORM,
    }));

    await trigger(["title", "fileExtension", "documentType"]);
  };

  const facilityId =
    dialogState.facilities?.length === 1
      ? dialogState.facilities[0].id
      : undefined;

  const { data: acceptableFileFormatsByDocumentTypeData } = useQuery(
    GET_ACCEPTABLE_FILE_FORMATS_BY_DOCUMENT_TYPE,
    {
      variables: {
        tenantId,
        facilityId: facilityId ?? "",
      },
      skip: !tenantId || !facilityId,
    }
  );

  const acceptableFileFormats = useMemo(() => {
    if (
      dialogState.documentType &&
      acceptableFileFormatsByDocumentTypeData?.getAcceptableFileFormatsByDocumentType
    ) {
      return (
        acceptableFileFormatsByDocumentTypeData.getAcceptableFileFormatsByDocumentType.find(
          (f: FileFormatsByDocumentType) =>
            f.documentType === dialogState.documentType
        )?.fileFormats ?? []
      );
    }

    return [];
  }, [
    dialogState.documentType,
    acceptableFileFormatsByDocumentTypeData?.getAcceptableFileFormatsByDocumentType,
  ]);

  // Wait until we have a user before rendering
  if (!user) {
    return null;
  }

  const onAddSubmit: SubmitHandler<DocumentFormData> = async (data) => {
    // Since the File type is immutable, create a new file
    // with the name from the form submission
    const file = dialogState.selectedFile
      ? new File(
          [dialogState.selectedFile],
          data.title ?? dialogState.selectedFile.name,
          { type: dialogState.selectedFile.type }
        )
      : undefined;

    const associations: DocumentAssociationsInput[] = getAssociations(
      data,
      tenantId,
      reportId,
      facilityId
    );

    const documentData = {
      id: data.id,
      tenantId: tenantId ?? "",
      documentType: data.documentType ?? DocumentType.Other,
      title: data.title ?? file?.name,
      description: data.description,
      documentTags: data.documentTags,
      authoredAt: data.authoredAt,
      storageLink: data.storageLink ?? undefined,
      fileExtension: data.fileExtension?.replace(".", "") ?? undefined,
    };

    await uploadFile({
      file,
      documentData,
      associations,
      currentSubmissionDocument:
        facilityDocumentIssues?.some(
          (i) =>
            i.key === "facilityDocuments" &&
            i.metadata.documentType === data.documentType
        ) ||
        dialogState.currentSubmissionDocument ||
        false,
    });
  };

  const onEditSubmit: SubmitHandler<DocumentFormData> = async (data) => {
    try {
      const associations: DocumentAssociationsInput[] = getAssociations(
        data,
        tenantId,
        reportId,
        facilityId
      );

      if (dialogState?.selectedFile) {
        // Since the File type is immutable, create a new file
        // with the name from the form submission
        const file = new File(
          [dialogState.selectedFile],
          data.title ?? dialogState.selectedFile.name,
          { type: dialogState.selectedFile.type }
        );

        await uploadFile({
          file,
          documentData: {
            id: dialogState.documentId,
            tenantId: tenantId ?? "",
            documentType: data.documentType ?? DocumentType.Other,
            title: file.name,
            description: data.description,
            documentTags: data.documentTags,
            authoredAt: data.authoredAt,
          },
          associations,
          currentSubmissionDocument:
            dialogState.currentSubmissionDocument ||
            data.currentSubmissionDocument ||
            false,
        });
      } else {
        setDialogState((state) => ({ ...state, saving: true }));
        const associationsToRemove = defaultValues.facilities?.filter(
          (existingFacility) =>
            !data.facilities?.some(
              (newFacility) => existingFacility.id === newFacility.id
            )
        );
        const documentId = await upsertDocument({
          documentData: {
            id: dialogState.documentId,
            tenantId: tenantId ?? "",
            documentType: data.documentType,
            title: data.title,
            description: data.description,
            documentTags: data.documentTags,
            fileExtension: data.fileExtension,
            storageLink: data.storageLink,
            authoredAt: data.authoredAt,
          },
          generateTitleOnCreate: generateTitleOnCreate,
          associations,
          removeAssociations:
            associationsToRemove?.map((f) => ({
              facilityId: f.id ?? "",
            })) ?? [],
          currentSubmissionDocument:
            dialogState.currentSubmissionDocument ||
            data.currentSubmissionDocument ||
            false,
        });
        const { data: upsertedDocumentData } = await fetchUpsertedDocument({
          variables: { id: documentId ?? "" },
        });
        const document = upsertedDocumentData?.document;

        if (
          hmbpFeature &&
          data.documentType &&
          isHmbpDocumentType(data.documentType)
        ) {
          await setDocumentOptionToUpload(data.documentType);
        }

        onClose();
        if (onSubmit) {
          onSubmit(
            document ?? {
              id: dialogState.documentId ?? "",
              title: data.title ?? dialogState?.selectedFile?.name ?? "",
              documentType: data.documentType ?? DocumentType.Other,
              fileExtension: data.fileExtension ?? "",
            },
            dialogState
          );
        }
        reset();
        setAttestation(false);
        alerts.success("File successfully edited");
      }
    } catch (err) {
      console.error(`${err}`);
      alerts.error("An error occurred while editing the document");
      setDialogState((state) => ({ ...state, saving: false }));
    }
  };

  const handleDocumentPick = (document: FacilityDocumentPickerItem) => {
    setPickedDocument(document);
    setDialogState((state) => ({
      ...state,
      documentId: document.id,
      title: document.title,
      documentType: document.documentType,
      fileExtension: document.fileExtension,
      storageLink: document.storageLink,
      description: document.description,
      authoredAt: document.authoredAt,
      step: FormState.FORM,
    }));
  };

  const handleDocumentTypeChange = (documentType: DocumentType) => {
    setDialogState((state) => ({
      ...state,
      documentType,
    }));
  };

  const showFacilityDocumentPicker =
    dialogState.mode === EditMode.ADD_OR_PICK && facilityId;

  const shouldShowAttestation =
    hasSelectedFile && !isReadOnly && showAttestation;

  const documentTypeLabel = dialogState.documentType
    ? documentTypeToLabel(dialogState.documentType)
    : "";

  let title = `${documentTypeLabel} Document`;
  if (!isReadOnly && dialogState.mode) {
    title = `${
      dialogState.mode === EditMode.ADD_OR_PICK ? "Add" : dialogState.mode
    } ${title}`;
  }

  const displayPreview =
    (hasSelectedFile || isLoadingDownloadLink) &&
    (watchFileExtension?.toLowerCase() === "pdf" ||
      dialogState?.fileExtension?.toLowerCase() === "pdf");

  let dialogWidth: Breakpoint = "xs";
  if (displayPreview) {
    dialogWidth = "lg";
  } else if (hasSelectedFile || isLoadingDownloadLink) {
    dialogWidth = "md";
  }

  return (
    <FormProvider {...form}>
      <Dialog
        open={dialogState.open}
        onClose={onClose}
        fullWidth
        maxWidth={dialogWidth}
        sx={
          displayPreview
            ? {
                "& .MuiDialog-paper": {
                  height: dialogHeight,
                },
              }
            : {}
        }
        title={title}
        noPrimaryAction={isReadOnly}
        primaryActionButtonProps={{
          onClick: handleSubmit(
            // TODO: We can consolidate these two handlers into one. The logic should be the same now that we are using the upsertDocument hook which handles adding, editing and file upload.
            dialogState.mode === "Add" ? onAddSubmit : onEditSubmit
          ),
          disabled: saveDisabled || hasInvalidFileExtension,
          loading: isUploading || dialogState.saving || loadingStateFields,
        }}
        additionalPrimaryActionElements={
          isReadOnly
            ? [
                <Button key="done-button" onClick={onClose}>
                  Done
                </Button>,
              ]
            : undefined
        }
        secondaryActionElements={[
          <Stack
            key="secondary-action-elements"
            direction="row"
            alignItems="center"
            gap={theme.spacing(1)}
          >
            {dialogState.documentId && (
              <Button
                endIcon={<Download />}
                onClick={async () => {
                  await fetchAndHandleDownload({
                    documentId: dialogState.documentId ?? "",
                  });
                  clearDownloadLink();
                }}
              >
                Download
              </Button>
            )}
            {shouldShowAttestation && (
              <Stack direction="row" alignItems="center">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={attestation}
                      onChange={(event) => setAttestation(event.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I reviewed and can confirm that all information and
                      documents provided are correct
                    </Typography>
                  }
                />
              </Stack>
            )}
          </Stack>,
        ]}
      >
        {dialogState.step === "Upload" ? ( // ast-grep-ignore
          <Stack spacing={2}>
            {showFacilityDocumentPicker && (
              <>
                <FacilityDocumentTypePicker
                  documentTypes={documentPickerOptions.map((o) => o.value)}
                  onSelectDocumentType={handleDocumentTypeChange}
                  selectedDocumentType={dialogState.documentType}
                />
                <FacilityDocumentPicker
                  facilityId={facilityId ?? ""}
                  documentTypes={documentPickerOptions.map((o) => o.value)}
                  onSelect={handleDocumentPick}
                  disabled={isUploading || dialogState.saving}
                  selectedDocumentType={dialogState.documentType}
                  acceptableFileFormats={acceptableFileFormats}
                  hasInvalidFileExtension={hasInvalidFileExtension}
                />
              </>
            )}
            <Dropzone
              label={"Drag and drop your document or click to select file"}
              dragActiveText="Drop the file here"
              onDrop={(acceptedFiles) => {
                handleFileSelect(acceptedFiles);
              }}
              multiple={false}
              sx={{
                minWidth: theme.spacing(48),
                minHeight: theme.spacing(29),
              }}
              disabled={isUploading || dialogState.saving}
            />
          </Stack>
        ) : (
          <Grid container spacing={0} columns={12}>
            <Grid item xs={12}>
              <Stack
                spacing={theme.spacing(5)}
                direction="row"
                justifyContent="space-between"
              >
                <Grid
                  item
                  lg={displayPreview ? 3 : 12}
                  xs={displayPreview ? 4 : 12}
                >
                  <Stack
                    gap={isReadOnly ? theme.spacing(3) : theme.spacing(1)}
                    sx={{ flex: 1 }}
                  >
                    <Grid container spacing={2} columns={12}>
                      <Grid item xs={11}>
                        {filenameStandardization.shouldShowCheckbox && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ pb: theme.spacing(1) }}
                          >
                            Filename will be standardized to match the format
                            "DocumentType_FacilityName_Date.fileExtension"
                          </Typography>
                        )}

                        {filenameStandardization.shouldShowCheckbox && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={
                                  filenameStandardization.keepOriginalFilename
                                }
                                onChange={(event) => {
                                  filenameStandardization.setKeepOriginalFilename(
                                    event.target.checked
                                  );
                                }}
                                disabled={shouldFieldsBeDisabled}
                              />
                            }
                            label="Keep original file name"
                          />
                        )}
                      </Grid>
                      <Grid item xs={11} mt={theme.spacing(1)}>
                        <FormTextField
                          name="title"
                          label="Filename *"
                          control={control}
                          sx={{ marginTop: theme.spacing(1) }}
                          disabled={shouldFieldsBeDisabled}
                        />
                      </Grid>
                      {!shouldFieldsBeDisabled && !isReadOnly && (
                        <Grid
                          item
                          xs={1}
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            paddingBottom: theme.spacing(2.5),
                          }}
                        >
                          <IconButton
                            onClick={() => {
                              setDialogState((state) => ({
                                ...state,
                                step: FormState.UPLOAD,
                                selectedFile: undefined,
                                fileExtension: undefined,
                              }));
                            }}
                          >
                            <FindReplaceIcon
                              color="primary"
                              fontSize="medium"
                            />
                          </IconButton>
                        </Grid>
                      )}
                    </Grid>

                    <div style={{ display: "none" }}>
                      <FormTextField
                        name="fileExtension"
                        hideErrors
                        label="Format"
                        control={control}
                        disabled={shouldFieldsBeDisabled}
                      />
                    </div>

                    <div style={{ display: "none" }}>
                      <FormTextField
                        name="storageLink"
                        hideErrors
                        label="Storage Link"
                        control={control}
                        disabled={shouldFieldsBeDisabled}
                      />
                    </div>

                    <div style={{ display: "none" }}>
                      <FormTextField
                        name="currentSubmissionDocument"
                        hideErrors
                        label="Current Submission Document"
                        control={control}
                        disabled={shouldFieldsBeDisabled}
                      />
                    </div>

                    {issues?.find((issue) => issue.key === "fileSize") && (
                      <Typography
                        color="error"
                        sx={{ mt: 0, pt: 0, mb: theme.spacing(1) }}
                      >
                        {
                          issues?.find((issue) => issue.key === "fileSize")
                            ?.message
                        }
                      </Typography>
                    )}
                    <FormAutocomplete
                      label="Document Type *"
                      control={control}
                      name="documentType"
                      autocompleteItems={documentPickerOptions}
                      disabled={
                        shouldFieldsBeDisabled || disableDocumentTypeSelection
                      }
                    />
                    {isInvalidSds && (
                      <Typography
                        color="error"
                        sx={{ mt: 0, pt: 0, mb: theme.spacing(2) }}
                      >
                        Safety Data Sheets must be in PDF format
                      </Typography>
                    )}
                    {showHmbpWarning && (
                      <Typography
                        color="error"
                        sx={{ mt: 0, pt: 0, mb: theme.spacing(2) }}
                      >
                        {`${
                          prettyPrintDocumentType(dialogState.documentType) ??
                          "This document type"
                        } was previously designated as "${documentOption}" and will be changed to the "Upload Document" option.`}
                      </Typography>
                    )}
                    <DateField
                      name="authoredAt"
                      label="Date Authored"
                      control={control}
                      disabled={shouldFieldsBeDisabled}
                    />

                    <FormTextField
                      name="description"
                      label="Description"
                      control={control}
                      disabled={shouldFieldsBeDisabled}
                      textFieldProps={{
                        multiline: true,
                        rows: 3,
                      }}
                    />

                    {activitySelectItems && (
                      <FormSelect
                        name="activityId"
                        label="Activity"
                        control={control}
                        selectItems={activitySelectItems}
                        disabled={shouldFieldsBeDisabled}
                      />
                    )}
                    {showTagPicker && (
                      <Controller
                        name="documentTags"
                        control={control}
                        render={({ field, fieldState }) => (
                          <TagPicker
                            {...field}
                            {...fieldState}
                            tagType={TagType.Document}
                            label="Tags"
                            disabled={shouldFieldsBeDisabled}
                          />
                        )}
                      />
                    )}
                    <Controller
                      name="facilities"
                      control={control}
                      render={({ field, fieldState }) => (
                        <MultipleFacilityPicker
                          {...field}
                          {...fieldState}
                          sx={{
                            display: showFacilityPicker ? "block" : "none",
                            minHeight: theme.spacing(16.5),
                          }}
                          value={
                            field.value?.map((f) => ({
                              id: f.id ?? "",
                              name: f.name ?? "",
                            })) ?? []
                          }
                          defaultSearchTerm={`tenantId:${tenantId}`}
                          label="Facilities"
                          onChange={(facilities) => {
                            field.onChange(facilities ?? []);
                          }}
                          disabled={shouldFieldsBeDisabled}
                        />
                      )}
                    />
                  </Stack>
                </Grid>
                {displayPreview && (
                  <Grid item lg={9} xs={8}>
                    <Box
                      sx={{
                        paddingTop: theme.spacing(1),
                        height: "calc(100vh - 230px)",
                        maxHeight: `${dialogHeight - 165}px`,
                      }}
                    >
                      <DocumentPreview fileUrl={fileUrl} />
                    </Box>
                  </Grid>
                )}
              </Stack>
            </Grid>
          </Grid>
        )}
      </Dialog>
    </FormProvider>
  );
}

function getAssociations(
  data: DocumentFormData,
  tenantId: string,
  reportId: string | undefined,
  facilityId: string | undefined
): DocumentAssociationsInput[] {
  return [
    ...(data.facilities ?? []).map((f) => ({
      tenantId: tenantId ?? "",
      facilityId: f.id,
    })),
    ...(reportId
      ? [
          {
            tenantId: tenantId ?? "",
            facilityId: facilityId ?? "",
            reportId,
          },
        ]
      : []),
    ...(data.activityId ? [{ activityId: data.activityId }] : []),
  ];
}
